import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
} from "react-router-dom";
import { guestApi, type GuestRoomInfo } from "@/api/meet-api";
import {
  beginLogin,
  completeLogin,
  guestSession,
  isSignedIn,
  setOrg,
} from "@/auth/session";
import { GuestEntry } from "@/components/GuestEntry";

// Mỗi trang một gói: trang chủ không phải tải mediasoup, trang họp không tải form đặt lịch.
const HomePage = lazy(() =>
  import("@/pages/HomePage").then((m) => ({ default: m.HomePage })),
);
const RoomPage = lazy(() =>
  import("@/pages/RoomPage").then((m) => ({ default: m.RoomPage })),
);

// Mở từ app AloUbot kèm `?org=<mã tổ chức>`: dùng đúng tổ chức đang chọn bên chat.
const params = new URLSearchParams(location.search);
if (params.get("org")) setOrg(params.get("org"));

function RequireLogin({ children }: { children: ReactNode }) {
  const signedIn = isSignedIn();
  useEffect(() => {
    if (!signedIn) void beginLogin();
  }, [signedIn]);
  return signedIn ? (
    children
  ) : (
    <div className="mu-center mu-muted">
      Đang chuyển tới trang đăng nhập AloUbot…
    </div>
  );
}

/**
 * Trang một cuộc họp. Chưa đăng nhập: cuộc họp nhận khách ngoài thì cho nhập tên xin vào (hoặc
 * đăng nhập), không nhận thì đi đăng nhập AloUbot như mọi trang khác.
 */
function RoomGate() {
  const { code = "" } = useParams();
  // Phiên khách dùng được cả ở nhóm nhỏ chia ra từ phòng của nó (máy chủ kiểm phạm vi).
  const member = isSignedIn() || guestSession() !== null;
  const [entered, setEntered] = useState(member);
  const [guestRoom, setGuestRoom] = useState<GuestRoomInfo | null>(null);

  useEffect(() => {
    if (entered) return;
    let cancelled = false;
    guestApi
      .info(code.toLowerCase())
      .then((info) => {
        if (cancelled) return;
        if (info.allowed && info.room) setGuestRoom(info.room);
        else void beginLogin();
      })
      .catch(() => !cancelled && void beginLogin());
    return () => {
      cancelled = true;
    };
  }, [code, entered]);

  if (entered) return <RoomPage />;
  if (guestRoom)
    return <GuestEntry room={guestRoom} onEntered={() => setEntered(true)} />;
  return <div className="mu-center mu-muted">Đang mở cuộc họp…</div>;
}

function Callback() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    completeLogin(location.search)
      .then((returnTo) => navigate(returnTo, { replace: true }))
      .catch((e: Error) => setError(e.message));
  }, [navigate]);
  if (!error) return <div className="mu-center mu-muted">Đang đăng nhập…</div>;
  return (
    <div className="mu-center">
      <p>{error}</p>
      <button
        className="mu-btn mu-btn--primary"
        onClick={() => void beginLogin("/")}
      >
        Đăng nhập lại
      </button>
    </div>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<div className="mu-center mu-muted">Đang tải…</div>}>
        <Routes>
          <Route path="/auth/callback" element={<Callback />} />
          <Route
            path="/"
            element={
              <RequireLogin>
                <HomePage />
              </RequireLogin>
            }
          />
          <Route path="/r/:code" element={<RoomGate />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
